/**
 * Iter105: Globale Tastatur-Shortcuts (Gmail-style sequence shortcuts).
 *
 * Sequenz-Shortcuts (innerhalb 1.5s):
 *   G+D → Dashboard
 *   G+R → Belege (Receipts)
 *   G+I → Rechnungen (Invoices)
 *   G+B → Eingangsrechnungen (Bills)
 *   G+J → Journal
 *   G+K → Kasse (Cashbook)
 *   G+L → Lohn (Payroll)
 *   G+T → Treuhänder/Reports
 *   G+S → Suche (= ⌘K)
 *
 * Direkt-Shortcuts:
 *   ⌘K / Ctrl+K → Command Palette
 *   N          → Neu (context-aware via Action im Listener)
 *   /          → Fokus Suchfeld
 *   Shift+?    → Tastatur-Cheatsheet öffnen
 *   Esc        → Schliessen
 *
 * Wird ignoriert wenn der Fokus auf <input>, <textarea>, <select> oder contenteditable liegt.
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export const SHORTCUT_MAP = [
  { sequence: ["g", "d"], path: "/dashboard", label: "Dashboard" },
  { sequence: ["g", "r"], path: "/receipts", label: "Belege" },
  { sequence: ["g", "i"], path: "/invoices", label: "Kundenrechnungen" },
  { sequence: ["g", "b"], path: "/bills", label: "Eingangsrechnungen" },
  { sequence: ["g", "j"], path: "/journal", label: "Journal" },
  { sequence: ["g", "k"], path: "/cashbook", label: "Kassenbuch" },
  { sequence: ["g", "l"], path: "/payroll", label: "Lohn" },
  { sequence: ["g", "t"], path: "/reports", label: "Reports" },
  { sequence: ["g", "u"], path: "/dunning", label: "Mahnwesen" },
  { sequence: ["g", "n"], path: "/bank-reconciliation", label: "Bankabstimmung" },
  { sequence: ["g", "c"], path: "/contacts", label: "Kontakte" },
  { sequence: ["g", "a"], path: "/assets", label: "Anlagevermögen" },
];

const SEQUENCE_TIMEOUT_MS = 1500;

function shouldIgnoreTarget(target) {
  if (!target) return false;
  const tag = target.tagName?.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  // Innerhalb eines Dialogs trotzdem zulassen für Escape, aber nicht für Buchstaben-Sequenzen
  return false;
}

export function useKeyboardShortcuts({ onOpenSearch, onOpenCheatsheet }) {
  const navigate = useNavigate();
  const sequenceRef = useRef([]);
  const timerRef = useRef(null);

  const clearSequence = useCallback(() => {
    sequenceRef.current = [];
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      // Modifier-Tasten → meist Browser-Shortcuts, nicht stören
      if (e.altKey) return;

      // ⌘K / Ctrl+K → Search (wird zentral hier behandelt)
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenSearch?.();
        return;
      }

      // Shift+? → Cheatsheet (im Browser ist das `?`)
      if (e.key === "?" && !e.metaKey && !e.ctrlKey) {
        if (shouldIgnoreTarget(e.target)) return;
        e.preventDefault();
        onOpenCheatsheet?.();
        return;
      }

      // Escape → Sequenz abbrechen (Dialog-Close erledigen Dialoge selbst)
      if (e.key === "Escape") {
        clearSequence();
        return;
      }

      // Restliche Shortcuts nur wenn nicht in Eingabe
      if (shouldIgnoreTarget(e.target)) return;
      // Reine Modifier-Drücke ignorieren
      if (e.metaKey || e.ctrlKey) return;

      const key = e.key.toLowerCase();
      // Nur Single-Char-Keys verarbeiten
      if (key.length !== 1) return;

      sequenceRef.current.push(key);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(clearSequence, SEQUENCE_TIMEOUT_MS);

      const seq = sequenceRef.current;
      // Prüfe ob Sequenz mit einem Shortcut übereinstimmt
      const match = SHORTCUT_MAP.find(
        (s) => s.sequence.length === seq.length && s.sequence.every((c, i) => c === seq[i])
      );
      if (match) {
        e.preventDefault();
        navigate(match.path);
        clearSequence();
        return;
      }
      // Prüfe ob mind. ein Shortcut die Sequenz als Präfix hat
      const isPrefix = SHORTCUT_MAP.some(
        (s) => seq.length < s.sequence.length && s.sequence.slice(0, seq.length).every((c, i) => c === seq[i])
      );
      if (!isPrefix) {
        // Sequenz passt zu nichts → resetten und ersten Buchstaben als neuen Start versuchen
        sequenceRef.current = [key];
        if (
          !SHORTCUT_MAP.some((s) => s.sequence[0] === key)
        ) {
          clearSequence();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [navigate, onOpenSearch, onOpenCheatsheet, clearSequence]);
}

/** Cheatsheet-Modal-State Hook */
export function useCheatsheet() {
  const [open, setOpen] = useState(false);
  return { open, setOpen };
}
